import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { Product } from "./types";

interface CatalogContextValue {
  products: Product[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  getProduct: (idOrSlug: string) => Product | undefined;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);
const CATALOG_CACHE_KEY = "socrattica:public-catalog:v1";

function dateToIso(value: unknown) {
  if (value && typeof value === "object" && "toDate" in value) {
    const toDate = (value as { toDate?: unknown }).toDate;
    if (typeof toDate === "function") {
      return (toDate.call(value) as Date).toISOString();
    }
  }

  return typeof value === "string" ? value : undefined;
}

function normalizeProduct(id: string, data: Record<string, unknown>): Product {
  const currency = ["COP", "USD", "EUR", "MXN"].includes(String(data.currency))
    ? (data.currency as Product["currency"])
    : "COP";

  return {
    ...(data as unknown as Product),
    id,
    currency,
    createdAt: dateToIso(data.createdAt),
    updatedAt: dateToIso(data.updatedAt),
    publishedAt: dateToIso(data.publishedAt) ?? String(data.publishedAt ?? ""),
  };
}

export async function searchCatalog(query: string, cursor?: string) {
  const params = new URLSearchParams({ q: query, pageSize: "50" });
  if (cursor) params.set("cursor", cursor);
  const response = await fetch(`/api/catalog?${params}`, {
    headers: { Accept: "application/json" },
  });
  const data = (await response.json().catch(() => null)) as {
    products?: Array<Record<string, unknown>>;
    nextCursor?: string;
    hasMore?: boolean;
    message?: string;
  } | null;
  if (!response.ok || !data?.products) {
    throw new Error(data?.message ?? "No pudimos buscar en la librería.");
  }
  return {
    products: data.products
      .map((product) => normalizeProduct(String(product.id ?? ""), product))
      .filter((product) => Boolean(product.id && product.slug && product.title)),
    nextCursor: data.nextCursor,
    hasMore: data.hasMore === true,
  };
}

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [nextCursor, setNextCursor] = useState<string>();
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    let hasCachedProducts = false;

    try {
      const cached = window.sessionStorage.getItem(CATALOG_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as { products?: Product[] };
        if (Array.isArray(parsed.products)) {
          hasCachedProducts = true;
          setProducts(parsed.products);
          setIsLoading(false);
        }
      }
    } catch {
      // El catálogo funciona aunque el navegador bloquee sessionStorage.
    }

    void fetch("/api/catalog", {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        const data = (await response.json().catch(() => null)) as {
          products?: Array<Record<string, unknown>>;
          nextCursor?: string;
          hasMore?: boolean;
          message?: string;
        } | null;
        if (!response.ok || !data?.products) {
          throw new Error(data?.message ?? "No pudimos cargar la librería.");
        }
        const next = data.products
          .map((product) => normalizeProduct(String(product.id ?? ""), product))
          .filter((product) => Boolean(product.id && product.slug && product.title))
          .sort((a, b) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""));

        if (!active) return;
        setProducts(next);
        setNextCursor(data.nextCursor);
        setHasMore(data.hasMore === true);
        setError(null);
        setIsLoading(false);
        try {
          window.sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({ products: next }));
        } catch {
          // La caché es una mejora, no un requisito.
        }
      })
      .catch((requestError: unknown) => {
        if (
          !active ||
          (requestError instanceof DOMException && requestError.name === "AbortError")
        ) {
          if (active && !hasCachedProducts) {
            setError("La librería tardó demasiado en responder. Inténtalo nuevamente.");
            setIsLoading(false);
          }
          return;
        }
        if (!hasCachedProducts) {
          setError(
            requestError instanceof Error
              ? requestError.message
              : "No pudimos conectar con el catálogo.",
          );
          setIsLoading(false);
        }
      })
      .finally(() => window.clearTimeout(timeout));

    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [refreshKey]);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setNextCursor(undefined);
    setHasMore(false);
    setRefreshKey((current) => current + 1);
  }, []);

  const loadMore = useCallback(() => {
    if (!nextCursor || !hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    const params = new URLSearchParams({ cursor: nextCursor, pageSize: "24" });
    void fetch(`/api/catalog?${params}`, {
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        const data = (await response.json().catch(() => null)) as {
          products?: Array<Record<string, unknown>>;
          nextCursor?: string;
          hasMore?: boolean;
          message?: string;
        } | null;
        if (!response.ok || !data?.products) {
          throw new Error(data?.message ?? "No pudimos cargar más productos.");
        }
        const additional = data.products
          .map((product) => normalizeProduct(String(product.id ?? ""), product))
          .filter((product) => Boolean(product.id && product.slug && product.title));
        setProducts((current) => {
          const merged = new Map(current.map((product) => [product.id, product]));
          for (const product of additional) merged.set(product.id, product);
          const next = Array.from(merged.values()).sort((a, b) =>
            (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""),
          );
          try {
            window.sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({ products: next }));
          } catch {
            // La caché no es necesaria para continuar navegando.
          }
          return next;
        });
        setNextCursor(data.nextCursor);
        setHasMore(data.hasMore === true);
      })
      .catch((requestError: unknown) => {
        setError(
          requestError instanceof Error ? requestError.message : "No pudimos cargar más productos.",
        );
      })
      .finally(() => setIsLoadingMore(false));
  }, [hasMore, isLoadingMore, nextCursor]);

  const value = useMemo<CatalogContextValue>(
    () => ({
      products,
      isLoading,
      isLoadingMore,
      hasMore,
      error,
      refresh,
      loadMore,
      getProduct: (idOrSlug) =>
        products.find((product) => product.id === idOrSlug || product.slug === idOrSlug),
    }),
    [error, hasMore, isLoading, isLoadingMore, loadMore, products, refresh],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
  const context = useContext(CatalogContext);

  if (!context) {
    throw new Error("useCatalog debe usarse dentro de CatalogProvider.");
  }

  return context;
}
